/**
 * Thân gửi máy chủ của form "Nhập số dư cũ" (STT 2.3, ADR-090). Hàm thuần để kiểm bằng test đơn vị.
 *
 * Lỗi 10/10 nằm ở đây: ô "Giá trị còn lại" để trống bị đổi thành '0' → gói sổ cũ 0đ → thợ không có hoa hồng
 * ở mọi buổi mà không ai biết. Quy tắc: TRỐNG ≠ 0. `giaTriConVnd` chỉ là '0' khi người nhập tích "Gói tặng"
 * (và khi đó gửi kèm `tang: true`); ngoài ra ô trống / '0' → `null` (nút Lưu khoá). Máy chủ kiểm lại.
 *
 * V1: giá trị dương nhỏ hơn số buổi × 1.000đ cũng bị khoá (cùng hàm `loiGiaTriConLaiSoCu` với máy chủ) — giá trị
 * buổi làm tròn xuống 1.000đ nên phần còn lại dồn hết vào buổi đầu, các buổi sau thợ 0đ hoa hồng.
 */
import { giaTriToiThieuSoCu, loiGiaTriConLaiSoCu } from '@sapa/shared'

/** Trần một lần nhập (khớp `TRAN_VND` của máy chủ): 100.000.000đ. */
const TRAN_VND = 100_000_000n
const SO_BUOI_TOI_DA = 150

export interface FormSoDuCu {
  branchId: string
  lyDo: string
  /** Dịch vụ của gói ('' = chưa chọn). */
  variant: string
  /** Số buổi còn (chỉ chữ số, '' = chưa gõ). */
  soBuoi: string
  /** Giá trị còn lại, chuỗi đồng ('' = TRỐNG, chưa nhập). */
  giaTri: string
  /** Gói khách được tặng: giá trị 0đ, thợ không có hoa hồng. */
  goiTang: boolean
  /** Hạn dùng đến (yyyy-MM-dd, ngày VN). */
  het: string
  /** Hôm nay (yyyy-MM-dd, ngày VN) — hạn phải sau ngày này. */
  homNay: string
  /** Ví: tiền khách đã nạp còn lại / tiền tặng còn lại, chuỗi đồng ('' = không có). */
  that: string
  tangVi: string
  /** Đã thấy cảnh báo "khách đã có số dư cũ" và vẫn nhập thêm. */
  xacNhanThem?: boolean
}

export interface ThanSoDuCu {
  branchId: string
  note: string
  xacNhanThem?: true
  goi: {
    serviceVariantId: string
    soBuoiCon: number
    giaTriConVnd: string
    hetHan: string
    tang?: true
  } | null
  vi: { cashVnd: string; bonusVnd: string } | null
}

/** Chuỗi đồng hợp lệ: chữ số, bội 1.000, không quá trần. '0' hợp lệ ở đây (người gọi tự quyết 0 có được không). */
function laTienHopLe(x: string): boolean {
  return /^\d{1,12}$/.test(x) && BigInt(x) % 1000n === 0n && BigInt(x) <= TRAN_VND
}

export function thanSoDuCu(f: FormSoDuCu): ThanSoDuCu | null {
  const lyDo = f.lyDo.trim()
  if (lyDo.length < 2) return null

  const soBuoi = /^\d{1,3}$/.test(f.soBuoi) ? Number.parseInt(f.soBuoi, 10) : 0
  const coGoi = f.variant !== '' && soBuoi > 0
  const that = f.that.trim()
  const tangVi = f.tangVi.trim()
  const coVi = (that !== '' && that !== '0') || (tangVi !== '' && tangVi !== '0')
  if (!coGoi && !coVi) return null

  let goi: ThanSoDuCu['goi'] = null
  if (coGoi) {
    if (soBuoi > SO_BUOI_TOI_DA) return null
    if (f.het === '' || f.het <= f.homNay) return null
    if (f.goiTang) {
      // Gói tặng: 0đ có chủ ý, bất kể ô giá trị còn chữ gì.
      goi = {
        serviceVariantId: f.variant,
        soBuoiCon: soBuoi,
        giaTriConVnd: '0',
        hetHan: f.het,
        tang: true,
      }
    } else {
      const giaTri = f.giaTri.trim()
      // TRỐNG hay '0' mà không tích Gói tặng: chưa đủ — KHÔNG được thay bằng '0'.
      if (giaTri === '' || giaTri === '0' || !laTienHopLe(giaTri)) return null
      if (loiGiaTriConLaiSoCu(BigInt(giaTri), soBuoi, false) !== null) return null
      goi = { serviceVariantId: f.variant, soBuoiCon: soBuoi, giaTriConVnd: giaTri, hetHan: f.het }
    }
  }

  let vi: ThanSoDuCu['vi'] = null
  if (coVi) {
    // Ở ví, hai ô là "tiền thật" và "tiền tặng": ô bỏ trống là 0 đúng nghĩa (đã có ô kia > 0).
    const cash = that === '' ? '0' : that
    const bonus = tangVi === '' ? '0' : tangVi
    if (!laTienHopLe(cash) || !laTienHopLe(bonus)) return null
    vi = { cashVnd: cash, bonusVnd: bonus }
  }

  return {
    branchId: f.branchId,
    note: lyDo,
    ...(f.xacNhanThem === true ? { xacNhanThem: true as const } : {}),
    goi,
    vi,
  }
}

/**
 * Lỗi giá trị cần NÓI RA bằng chữ ở form (ngoài việc khoá nút Lưu): giá trị dương nhưng quá nhỏ so với số buổi.
 * Ô trống / '0' đã có câu riêng ("Nhập giá trị còn lại…"); chưa chọn gói / chưa gõ số buổi / Gói tặng thì chưa báo.
 */
export function loiGiaTriForm(
  f: Pick<FormSoDuCu, 'variant' | 'soBuoi' | 'giaTri' | 'goiTang'>,
): { loai: 'qua-nho'; toiThieuVnd: string } | null {
  if (f.goiTang || f.variant === '') return null
  const soBuoi = /^\d{1,3}$/.test(f.soBuoi) ? Number.parseInt(f.soBuoi, 10) : 0
  const giaTri = f.giaTri.trim()
  if (soBuoi < 1 || soBuoi > SO_BUOI_TOI_DA || !/^\d{1,12}$/.test(giaTri)) return null
  const v = BigInt(giaTri)
  if (v === 0n) return null
  return loiGiaTriConLaiSoCu(v, soBuoi, false) === 'qua-nho'
    ? { loai: 'qua-nho', toiThieuVnd: giaTriToiThieuSoCu(soBuoi).toString() }
    : null
}
